import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Loader2, Tags } from "lucide-react";
import { useCreateClassificationOnlyJob } from "@/features/admin/queries/useCollectionJobs";

interface ClassificationOnlyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClassificationOnlyDialog({
  open,
  onOpenChange,
}: ClassificationOnlyDialogProps) {
  const createJob = useCreateClassificationOnlyJob();

  const handleSubmit = async () => {
    try {
      await createJob.mutateAsync({
        filters: {},
      });
      onOpenChange(false);
    } catch {
      // handled by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-violet-600" />
            Run Classification
          </DialogTitle>
          <DialogDescription>
            Re-classify all existing profiles using the current classification
            rules. No new data is imported from CoreSignal.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
          <p className="text-sm text-ink-600">
            This will re-run the classification engine on all imported profiles
            to apply any updated rules.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createJob.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createJob.isPending}>
            {createJob.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Tags className="h-4 w-4 mr-2" />
            )}
            Run Classification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
